import { useEffect, useState } from "react";
import { X, Download, Share, Plus } from "lucide-react";
import {
  listenInstallPrompt,
  promptInstall,
  isStandalone,
  isIOS,
} from "@/lib/pwa";

const DISMISS_KEY = "rammy:pwa:dismissed-at";
const INSTALLED_KEY = "rammy:pwa:installed";
const DISMISS_WINDOW_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

function recentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return ts && Date.now() - ts < DISMISS_WINDOW_MS;
  } catch (_) {
    return false;
  }
}

/**
 * Install-app banner. Renders as a bottom sheet on mobile / a floating card on desktop.
 * - Chrome/Edge/Android: uses beforeinstallprompt.
 * - iOS Safari: shows manual "Share -> Add to Home Screen" instructions.
 * - Auto-hides once installed, in standalone mode, or if dismissed in the last 7 days.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  // "Engaged" = this visitor has actually sent a message, or this isn't
  // their first visit. An install card parked over the send button during
  // someone's first thirty seconds is friction, not activation (QA D12).
  const [engaged, setEngaged] = useState(() => {
    try {
      if (sessionStorage.getItem("rammy_engaged") === "1") return true;
      const visits = Number(localStorage.getItem("rammy_visits") || "0");
      return visits >= 2;
    } catch (_) { return false; }
  });

  useEffect(() => {
    try {
      if (sessionStorage.getItem("rammy_visit_counted") !== "1") {
        const visits = Number(localStorage.getItem("rammy_visits") || "0") + 1;
        localStorage.setItem("rammy_visits", String(visits));
        sessionStorage.setItem("rammy_visit_counted", "1");
        if (visits >= 2) setEngaged(true);
      }
    } catch (_) { /* private mode */ }
    const onEngaged = () => setEngaged(true);
    window.addEventListener("rammy:engaged", onEngaged);
    return () => window.removeEventListener("rammy:engaged", onEngaged);
  }, []);

  useEffect(() => {
    if (isStandalone()) return;
    let installed = false;
    try { installed = localStorage.getItem(INSTALLED_KEY) === "1"; } catch (_) {}
    if (installed) return;
    if (recentlyDismissed()) return;

    const unsub = listenInstallPrompt((evt) => {
      setDeferred(evt);
      if (evt) setVisible(true);
    });

    // iOS Safari: no beforeinstallprompt, so we surface a manual hint after a short delay.
    if (isIOS()) {
      const t = setTimeout(() => setVisible(true), 4000);
      return () => { unsub(); clearTimeout(t); };
    }
    return unsub;
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
    setVisible(false);
    setShowIOSHelp(false);
  };

  const install = async () => {
    if (deferred) {
      const res = await promptInstall();
      if (res?.outcome === "accepted") setVisible(false);
      else dismiss();
      return;
    }
    if (isIOS()) {
      setShowIOSHelp(true);
      return;
    }
    dismiss();
  };

  if (!visible || !engaged) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed z-[70] left-3 right-3 bottom-3 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[380px]"
    >
      <div className="relative rounded-2xl border border-white/10 bg-[#0f1116]/95 backdrop-blur-xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] overflow-hidden">
        {/* accent glow */}
        <div className="pointer-events-none absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#48e0c8]/20 blur-3xl" />
        <button
          data-testid="pwa-install-dismiss"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="absolute top-2 right-2 p-1.5 rounded-full text-neutral-400 hover:text-white hover:bg-white/10 transition"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <img
              src="/icon-192.png"
              alt="Rammy"
              className="w-11 h-11 rounded-xl border border-white/10"
            />
            <div className="flex-1 min-w-0">
              <div className="text-white text-[15px] font-medium leading-tight">
                Install Rammy on your home screen
              </div>
              <div className="text-neutral-400 text-[13px] mt-1 leading-snug">
                One-tap access, full-screen, and instant open — just like a native app.
              </div>
            </div>
          </div>

          {!showIOSHelp && (
            <div className="mt-4 flex items-center gap-2">
              <button
                data-testid="pwa-install-cta"
                onClick={install}
                className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-xl bg-[#48e0c8] text-[#0B0C10] text-sm font-medium hover:bg-[#5be9d3] transition"
              >
                <Download className="w-4 h-4" />
                {isIOS() ? "How to install" : "Add to Home Screen"}
              </button>
              <button
                data-testid="pwa-install-later"
                onClick={dismiss}
                className="h-10 px-3 rounded-xl text-neutral-300 text-sm hover:bg-white/5 transition"
              >
                Later
              </button>
            </div>
          )}

          {showIOSHelp && (
            <div
              data-testid="pwa-ios-help"
              className="mt-4 space-y-2 text-[13px] text-neutral-300"
            >
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/10 text-[11px] font-mono">1</span>
                <span>Tap the <Share className="inline w-4 h-4 -mt-0.5 text-[#48e0c8]" /> Share icon in Safari.</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/10 text-[11px] font-mono">2</span>
                <span>Choose <span className="text-white">Add to Home Screen</span> <Plus className="inline w-4 h-4 -mt-0.5" />.</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/10 text-[11px] font-mono">3</span>
                <span>Tap <span className="text-white">Add</span> — Rammy will appear on your home screen.</span>
              </div>
              <button
                data-testid="pwa-ios-dismiss"
                onClick={dismiss}
                className="mt-2 w-full h-9 rounded-lg bg-white/5 text-neutral-300 text-sm hover:bg-white/10 transition"
              >
                Got it
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
